import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/shared-ui/Button';
import { Input } from '@/shared-ui/Input';
import { Logo } from '@/shared-ui/Logo';
import { useAuthStore } from '@/core/stores/authStore';
import { authApi } from '@/core/api/endpoints/auth';
import { ApiError } from '@/core/api/types';
import { cn } from '@/utils/cn';

type Method = 'phone' | 'email';
type Stage = 'identifier' | 'code';

const PHONE_RE = /^1[3-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  const navigate = useNavigate();
  const loginWithPhone = useAuthStore((s) => s.loginWithPhone);
  const loginWithEmail = useAuthStore((s) => s.loginWithEmail);

  const [method, setMethod] = useState<Method>('phone');
  const [stage, setStage] = useState<Stage>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchMethod = (m: Method) => {
    setMethod(m);
    setStage('identifier');
    setIdentifier('');
    setCode('');
    setError(null);
  };

  const startCountdown = (seconds: number) => {
    setCountdown(seconds);
    const timer = window.setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  };

  const validateIdentifier = (): string | null => {
    const v = identifier.trim();
    if (!v) return '请输入' + (method === 'phone' ? '手机号' : '邮箱');
    if (method === 'phone' && !PHONE_RE.test(v)) return '手机号格式不正确';
    if (method === 'email' && !EMAIL_RE.test(v)) return '邮箱格式不正确';
    return null;
  };

  const sendCode = async (e: FormEvent) => {
    e.preventDefault();
    const err = validateIdentifier();
    if (err) return setError(err);
    setError(null);
    setLoading(true);
    try {
      if (method === 'phone') await authApi.sendSmsCode(identifier.trim());
      else await authApi.sendEmailCode(identifier.trim());
      setStage('code');
      startCountdown(60);
    } catch (e2) {
      setError(e2 instanceof ApiError ? e2.message : '发送失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(code.trim())) return setError('验证码格式不正确');
    setError(null);
    setLoading(true);
    try {
      if (method === 'phone') await loginWithPhone(identifier.trim(), code.trim());
      else await loginWithEmail(identifier.trim(), code.trim());
      navigate('/', { replace: true });
    } catch (e2) {
      setError(e2 instanceof ApiError ? e2.message : '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (countdown > 0) return;
    setError(null);
    setLoading(true);
    try {
      if (method === 'phone') await authApi.sendSmsCode(identifier.trim());
      else await authApi.sendEmailCode(identifier.trim());
      startCountdown(60);
    } catch (e2) {
      setError(e2 instanceof ApiError ? e2.message : '重发失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-6 overflow-hidden">
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-32 h-[480px] w-[480px] rounded-full bg-accent/30 blur-3xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[520px] w-[520px] rounded-full bg-accent-2/25 blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
      />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="relative w-full max-w-sm"
      >
        <div className="mb-8 text-center flex flex-col items-center gap-2">
          <Logo size={32} />
          <p className="text-sm text-fg-muted tracking-widest">声音 · 即 · 笔记</p>
        </div>

        <div className="mb-6 flex rounded-xl bg-bg-elevated p-1 border border-border">
          {(['phone', 'email'] as Method[]).map((m) => (
            <button
              key={m}
              onClick={() => switchMethod(m)}
              className={cn(
                'flex-1 h-9 rounded-lg text-sm font-medium transition-colors',
                method === m ? 'bg-bg text-fg shadow-sm' : 'text-fg-muted hover:text-fg'
              )}
            >
              {m === 'phone' ? '手机号' : '邮箱'}
            </button>
          ))}
        </div>

        {stage === 'identifier' ? (
          <form onSubmit={sendCode} className="space-y-3">
            <Input
              key={method}
              type={method === 'phone' ? 'tel' : 'email'}
              inputMode={method === 'phone' ? 'numeric' : 'email'}
              placeholder={method === 'phone' ? '请输入手机号' : '请输入邮箱'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoFocus
              maxLength={method === 'phone' ? 11 : 100}
            />
            {error && <p className="text-xs text-danger">{error}</p>}
            <Button type="submit" loading={loading} className="w-full">
              获取验证码
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3">
            <div className="text-xs text-fg-muted">
              验证码已发送至 <span className="text-fg">{identifier}</span>
              <button
                type="button"
                onClick={() => setStage('identifier')}
                className="ml-2 text-accent hover:underline"
              >
                修改
              </button>
            </div>
            <Input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="请输入验证码"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              maxLength={8}
            />
            {error && <p className="text-xs text-danger">{error}</p>}
            <Button type="submit" loading={loading} className="w-full">
              登录 / 注册
            </Button>
            <button
              type="button"
              onClick={resend}
              disabled={countdown > 0 || loading}
              className="w-full text-xs text-fg-muted hover:text-fg disabled:cursor-not-allowed"
            >
              {countdown > 0 ? `${countdown}s 后可重发` : '重新发送'}
            </button>
          </form>
        )}

        <p className="mt-10 text-center text-xs text-fg-subtle">
          登录即代表同意服务条款与隐私政策
        </p>
      </motion.div>
    </div>
  );
}
